import { useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { ChauffeurKasCard } from '../components/kas';
import { useKasStore } from '../stores';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Vandaag';
  if (d.toDateString() === yesterday.toDateString()) return 'Gisteren';

  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

export function KasPage() {
  const {
    chauffeurs, selectedDate, isLoading, isUpdating,
    totaalContant, totaalGecontroleerd, totaalOpen, openVorigeDagen,
    fetchTransacties, setDate, markeerGecontroleerd,
  } = useKasStore();

  useEffect(() => {
    fetchTransacties();
  }, [fetchTransacties]);

  return (
    <>
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-5">

          {/* Header with date navigation */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-heading font-semibold text-main">Kas</h1>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setDate(shiftDate(selectedDate, -1))}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-main min-w-[140px] text-center">
                  {formatDateLabel(selectedDate)}
                </span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setDate(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-muted"
                />
              </div>

              <button
                onClick={() => setDate(shiftDate(selectedDate, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Alert: open transactions from previous days */}
          {openVorigeDagen > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-amber-800">
                <strong>{openVorigeDagen}</strong> transactie{openVorigeDagen !== 1 ? 's' : ''} van vorige dagen nog niet gecontroleerd
              </span>
            </div>
          )}

          {/* Bewust géén kassaldo/stortingen hier: deze pagina is de werkvloer
              (medewerker tekent af wat de chauffeur inlevert). De stand van de
              kas en het storten zijn managementwerk en staan op /financieel. */}

          {/* Summary card */}
          <Card variant="elevated" padding="md">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted uppercase tracking-wider">Totaal contant</p>
                <p className="text-xl font-semibold text-main mt-1">{formatCurrency(totaalContant)}</p>
              </div>
              <div>
                <p className="text-xs text-muted uppercase tracking-wider">Gecontroleerd</p>
                <p className="text-xl font-semibold text-green-600 mt-1">{formatCurrency(totaalGecontroleerd)}</p>
              </div>
              <div>
                <p className="text-xs text-muted uppercase tracking-wider">Open</p>
                <p className="text-xl font-semibold text-amber-600 mt-1">{formatCurrency(totaalOpen)}</p>
              </div>
              <div>
                <p className="text-xs text-muted uppercase tracking-wider">Chauffeurs</p>
                <p className="text-xl font-semibold text-main mt-1">{chauffeurs.length}</p>
              </div>
            </div>
          </Card>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Chauffeur cards */}
          {!isLoading && chauffeurs.length > 0 && (
            <div className="space-y-3">
              {chauffeurs.map(chauffeur => (
                <ChauffeurKasCard
                  key={chauffeur.medewerkerIdSf}
                  chauffeur={chauffeur}
                  isUpdating={isUpdating === chauffeur.medewerkerIdSf}
                  onMarkeerGecontroleerd={markeerGecontroleerd}
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && chauffeurs.length === 0 && (
            <Card variant="bordered" padding="lg">
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                </svg>
                <p className="text-muted">Geen contante transacties op deze datum</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
